import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject, Subscription } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { APP_VERSION_NUMBER } from 'src/app/version';
import * as fromAuth from 'src/app/modules/authentication/store/auth';
import * as fromUserProfile from 'src/app/modules/core/store/user-profile';
import * as fromCop from 'src/app/modules/cop/store/cop/cop.actions';
import { GlobalEventListenerService } from 'src/app/modules/core/services/global-event-listener.service';
import { UserOrganization, UserProfile } from 'src/app/modules/core/store/user-profile/user-profile.model';
import { UserContext } from 'src/app/modules/authentication/store/auth';
import { Language } from '../../languages/language.model';
import { TranslateService } from '@ngx-translate/core';
import produce from 'immer';
import { OrgBusinessUnitsApiService } from '../../org-business-units/org-business-units-api.service';
import { OrgBusinessUnit } from '../../org-business-units/org-business-unit.model';
import { AuthApiService } from '@app/modules/authentication/store/auth/auth-api.service';
import { UserProfileApiService } from 'src/app/modules/core/store/user-profile';
import { Router } from '@angular/router';
import { UserProfileService } from '../../services/user-profile.service';
import { BroadcastService } from '@app/core/services/broadcast.service';

@Component({
    selector: 'topo-profile-menu',
    templateUrl: './profile-menu.component.html',
    styleUrls: ['./profile-menu.component.scss'],
    standalone: false
})
export class ProfileMenuComponent implements OnInit, OnDestroy {
  @ViewChild('profileMenu') profileMenu;
  clickSubscription: Subscription = null;
  destroy$ = new Subject<void>();
  users$: Observable<UserProfile[]>;
  userContext: UserContext;
  Permissions = fromAuth.Permissions;
  userOrganizations: UserOrganization[];
  languages: Language[];
  hasBusinessUnits: boolean;
  businessUnits: OrgBusinessUnit[];
  userHasMultipleProfiles: boolean;
  selectedOrganization: UserOrganization;
  selectedCultureCode: string;
  appVersionNumber: string;
  userProfile: UserProfile;
  shouldShowOpenSourceLicenses: boolean = false;
  selectedUnitUIds: string[];

  constructor(
    private store: Store<fromAuth.State>,
    private translateService: TranslateService,
    private eventListenerService: GlobalEventListenerService,
    private orgBusinessUnitsApiService: OrgBusinessUnitsApiService,
    private profileApiService: UserProfileApiService,
    private authApiService: AuthApiService,
    private userProfileService: UserProfileService,
    private router: Router,
    private broadcast: BroadcastService
  ) {}

  ngOnInit(): void {
    this.appVersionNumber = APP_VERSION_NUMBER;

    this.store
      .select(fromAuth.selectUserContext)
      .pipe(takeUntil(this.destroy$))
      .subscribe(userContext => {
        this.userContext = userContext;
      });

    this.store
      .select(fromUserProfile.selectProfileUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe(userProfile => {
        this.userProfile = userProfile;
        if (!!this.userProfile) {
          this.loadBusinessUnits();
          this.store.dispatch(fromUserProfile.changeAppLanguage({ cultureCode: this.userProfile.culture?.cultureCode }));
        }
        this.selectedCultureCode = userProfile?.culture?.cultureCode;
      });

    this.store
      .select(fromUserProfile.selectUserHasBusinessUnits)
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (!!this.userProfile) {
          this.loadBusinessUnits();
        }
      });

    this.store
      .select(fromUserProfile.selectUserLanguages)
      .pipe(takeUntil(this.destroy$))
      .subscribe(languages => {
        this.languages = languages;
      });

    this.store
      .select(fromUserProfile.selectUserOrganizations)
      .pipe(takeUntil(this.destroy$))
      .subscribe(userOrganizations => {
        userOrganizations = userOrganizations?.slice().sort(function (a, b) {
          if (a.organizationDisplayName < b.organizationDisplayName) {
            return -1;
          } else if (a.organizationDisplayName > b.organizationDisplayName) {
            return 1;
          } else {
            return 0;
          }
        });

        this.userOrganizations = userOrganizations;
        this.userHasMultipleProfiles = userOrganizations?.length > 1;
        this.selectedOrganization = this.userOrganizations?.find(o => o.organizationUId === this.userContext.organizationUId);
      });

    this.broadcast
      .listen('USER_PROFILE_REFRESH')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.store.dispatch(fromUserProfile.getUserProfile());
      });
  }

  private loadBusinessUnits() {
    this.hasBusinessUnits = this.userProfileService.getUserHasBusinessUnits();
    if (this.hasBusinessUnits) {
      const businessUnits = this.userProfileService.getUserBusinessUnits();
      this.processBusinessUnits(businessUnits);
    }
  }

  private processBusinessUnits(businessUnits: OrgBusinessUnit[]): void {
    const selectedUnitUIds = [];
    let rootUnit = businessUnits?.find(u => !!!u.parentUId);
    if (!!rootUnit) {
      rootUnit = produce(rootUnit, draftState => {
        draftState.name = this.translateService.instant('MANAGEMENT.MENU.GLOBAL-UNIT', { name: draftState.name });
      });
    }
    if (!!this.userProfile.businessUnitUId && businessUnits.find(u => u.uId === this.userProfile.businessUnitUId)) {
      selectedUnitUIds.push(this.userProfile.businessUnitUId);
    } else {
      if (!!rootUnit) {
        selectedUnitUIds.push(rootUnit.uId);
      }
    }

    this.selectedUnitUIds = selectedUnitUIds;
    this.businessUnits = businessUnits;
  }

  showHideProfileMenu(elm: any): void {
    if (elm.classList.contains('is-active')) {
      elm.classList.remove('is-active');

      this.unsubscribeToClicks();
    } else {
      elm.classList.add('is-active');

      this.subscribeToClicks();
    }
  }

  profileClick(elm: any): void {
    if (!this.userContext.isReadOnlyToken) {
      this.store.dispatch(fromUserProfile.showProfileForm({ showProfileForm: true }));
      this.showHideProfileMenu(elm);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  logout(): void {
    this.store.dispatch(fromAuth.logout());
  }

  subscribeToClicks() {
    if (this.clickSubscription) {
      return;
    }

    this.clickSubscription = this.eventListenerService.clickEvents$.pipe(takeUntil(this.destroy$)).subscribe(target => {
      if (!this.profileMenu.nativeElement.contains(target)) {
        this.profileMenu.nativeElement.classList.remove('is-active');
        this.unsubscribeToClicks();
      }
    });
  }

  unsubscribeToClicks() {
    if (this.clickSubscription) {
      this.clickSubscription.unsubscribe();
      this.clickSubscription = null;
    }
  }

  changeUserProfile() {
    if (!this.userContext.isReadOnlyToken) {
      this.store.dispatch(
        fromAuth.changeOrganization({
          organizationUId: this.selectedOrganization.organizationUId
        })
      );
    }
  }

  getLanguageDisplay(lang: Language) {
    if (!!lang) {
      return this.translateService.instant(`LANGUAGES.NATIVE.LANGUAGE-${lang.cultureCode}`);
    }
    return '';
  }

  languageSelected(): void {
    const userProfile = produce(this.userProfile, draftState => {
      draftState.culture.cultureCode = this.selectedCultureCode;
    });
    this.store.dispatch(fromUserProfile.saveUserProfile({ userProfile, close: true }));
  }

  onBusinessUnitsSelectionChanged(selectedUIds?: string[]): void {
    this.selectedUnitUIds = selectedUIds;
    const userProfile = produce(this.userProfile, draftState => {
      draftState.businessUnitUId = !!this.selectedUnitUIds && this.selectedUnitUIds.length ? this.selectedUnitUIds[0] : null;
    });
    this.profileApiService
      .updateUser(this.userContext.organizationUId, userProfile)
      .pipe(take(1))
      .subscribe(res => {
        this.store.dispatch(fromUserProfile.getUserProfileComplete({ result: res }));
        this.authApiService
          .refreshAccessToken()
          .pipe(take(1))
          .subscribe(token => {
            if (!!token) {
              this.store.dispatch(fromAuth.setToken({ token }));
              this.store.dispatch(fromAuth.permissionsChanged());
              if (this.router.url.includes('cop')) {
                this.store.dispatch(fromCop.refreshSelectedMap());
              }
            }
          });
      });
  }

  showOpenSourceLicenses() {
    this.shouldShowOpenSourceLicenses = true;
  }
}
